import { useEffect } from 'react';
import { useObserver } from 'mobx-react-lite';

import useUser from 'src/hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useCart from 'src/hooks/use-cart';
import { useGetRewards } from 'src/components/loyalty/use-get-rewards';
import { setLoyaltyData } from 'src/components/loyalty/loyalty.helpers';
import { useKioskPro } from './use-kiosk-pro';

type UseLoyaltyReturn = {
  loyaltyPoints: string;
  isAlpineIQEnabled: boolean;
  isDutchiePOSLoyaltyEnabled: boolean;
  isSpringbigEnabled: boolean;
};

export function useLoyalty(): UseLoyaltyReturn {
  const Cart = useCart();
  const User = useUser();
  const isLoggedIn = useObserver(() => User.isLoggedIn);
  const isLoading = useObserver(() => User.loading);
  const birthday = useObserver(() => User.birthday);
  const phone = useObserver(() => User.phone);
  const cartSubtotal = useObserver(() => Cart.chargeSubtotal);
  const isUserLoggedIn = isLoggedIn && !isLoading;

  const { dispensary } = useDispensary();
  const isKioskProEnabled = useKioskPro();
  const isShowLoyaltyFlagEnabled = (dispensary?.storeSettings?.showLoyaltyRewards ?? false) || isKioskProEnabled;
  const isAlpineIQEnabled = dispensary?.consumerDispensaryIntegrations?.alpineiq?.enabled ?? false;
  const isDutchiePOSLoyaltyEnabled = dispensary?.consumerDispensaryIntegrations?.dutchieposloyalty?.enabled ?? false;
  const isSpringbigEnabled = dispensary?.consumerDispensaryIntegrations?.springbig?.enabled ?? false;

  const customerId = User?.id;
  const pin = User.getLoyaltyPin(dispensary?.id);

  const skip =
    !isShowLoyaltyFlagEnabled ||
    !isUserLoggedIn ||
    !phone ||
    (isAlpineIQEnabled && !pin) ||
    (isDutchiePOSLoyaltyEnabled && !customerId) ||
    (isSpringbigEnabled && !customerId);

  const { data } = useGetRewards({
    dispensaryId: dispensary?.id,
    birthday,
    pin,
    phone,
    skip,
    customerId,
    cartSubtotal,
  });

  const rewardsData = data?.getRewardsV2;

  useEffect(() => {
    if (
      data &&
      (rewardsData?.auth?.pinConfirmed ||
        (isDutchiePOSLoyaltyEnabled && customerId) ||
        (isSpringbigEnabled && rewardsData?.userHasWallet))
    ) {
      setLoyaltyData({
        user: User,
        cart: Cart,
        data,
        dispensaryId: dispensary?.id,
      });
    }
  }, [data, rewardsData, isDutchiePOSLoyaltyEnabled, customerId, isSpringbigEnabled, User, Cart, dispensary?.id]);

  return {
    loyaltyPoints: rewardsData?.balance ?? '0',
    isAlpineIQEnabled,
    isDutchiePOSLoyaltyEnabled,
    isSpringbigEnabled,
  };
}
